import { useEffect, useState } from 'react';

export type ThemeChoice = 'system' | 'light' | 'dark';
export type ResolvedMode = 'light' | 'dark';

const STORAGE_KEY = 'nks.theme';

/**
 * Both themes are first-class (reference §6).
 *
 * Three-way choice, not a toggle: "system" is the default and is a real answer,
 * not the absence of one. Somebody whose laptop flips to dark at sunset expects
 * the app to follow, and a two-way toggle has no way to say that.
 *
 * Stored in localStorage rather than a cookie because it is read by a blocking
 * script before React exists — see the snippet in index.html. It is NOT PII and
 * it is not the customer cache, so it is exempt from the offline-cache
 * allowlist reasoning.
 */
export function readChoice(): ThemeChoice {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    // Private browsing, storage disabled. The system preference still works.
    return 'system';
  }
}

export function resolve(choice: ThemeChoice): ResolvedMode {
  if (choice !== 'system') return choice;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Applies the mode to `<html>`.
 *
 * `data-theme` rather than a class, so `tokens.css` can key off it and the
 * blocking script in index.html can set it before first paint without knowing
 * anything about the app's class conventions.
 */
export function apply(mode: ResolvedMode): void {
  document.documentElement.setAttribute('data-theme', mode);
}

/**
 * The theme, as a hook.
 *
 * Returns the resolved mode as well as the choice, because the AntD config is
 * built from the resolved one and the picker shows the choice — "system" is not
 * a colour and cannot be handed to a theme algorithm.
 */
export function useThemeMode(): {
  choice: ThemeChoice;
  mode: ResolvedMode;
  setChoice: (next: ThemeChoice) => void;
} {
  const [choice, setChoiceState] = useState<ThemeChoice>(() => readChoice());
  const [mode, setMode] = useState<ResolvedMode>(() => resolve(readChoice()));

  useEffect(() => {
    apply(mode);
  }, [mode]);

  // Follow the OS while the choice is "system". Without this the app keeps
  // whatever the laptop was doing at load and never catches up at sunset.
  useEffect(() => {
    if (choice !== 'system') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (): void => {
      setMode(media.matches ? 'dark' : 'light');
    };
    media.addEventListener('change', onChange);
    return () => {
      media.removeEventListener('change', onChange);
    };
  }, [choice]);

  const setChoice = (next: ThemeChoice): void => {
    setChoiceState(next);
    setMode(resolve(next));
    try {
      if (next === 'system') window.localStorage.removeItem(STORAGE_KEY);
      else window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Nothing to persist to. The choice still holds for this session.
    }
  };

  return { choice, mode, setChoice };
}
